/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Prompt Templates Catalog & Category Explorer View (Task 10.2.5 & 10.7.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useMemo, useState } from 'react';
import {
  ArrowRight,
  BookOpen,
  X,
  Zap,
  Sliders,
  CheckCircle2,
} from 'lucide-react';
import { SophiaIcon } from '@sofia/assets';
import { PromptCategoryFilterBar } from '../components/templates/PromptCategoryFilterBar';
import { PromptTemplateCard } from '../components/templates/PromptTemplateCard';
import { PromptTemplateRunnerView } from './PromptTemplateRunnerView';
import {
  PromptCatalogService,
  type EnrichedPromptTemplate,
} from '../services/prompt-catalog.service';
import { usePromptTemplateStore } from '../stores/usePromptTemplateStore';
import { useI18n } from '../stores/useI18nStore';

interface PromptTemplatesCatalogViewProps {
  onSelectTemplate?: (template: EnrichedPromptTemplate) => void;
  onNavigateToWizard?: () => void;
  onNavigateToEditor?: () => void;
}

export const PromptTemplatesCatalogView: React.FC<PromptTemplatesCatalogViewProps> = ({
  onSelectTemplate,
  onNavigateToWizard,
  onNavigateToEditor,
}) => {
  const {
    searchQuery,
    selectedCategory,
    selectedModel,
    activeTab,
    favorites,
    recentlyUsed,
    viewMode,
    resetFilters,
  } = usePromptTemplateStore();
  const { t } = useI18n();

  const [activeRunnerTemplate, setActiveRunnerTemplate] = useState<EnrichedPromptTemplate | null>(null);
  const [activePreviewTemplate, setActivePreviewTemplate] = useState<EnrichedPromptTemplate | null>(null);

  // Compute filtered templates
  const filteredTemplates = useMemo(() => {
    let list = PromptCatalogService.getAllTemplates();

    if (activeTab === 'favorites') {
      list = list.filter((tpl) => favorites.includes(tpl.slug));
    } else if (activeTab === 'recent') {
      const recentSlugs = recentlyUsed;
      list = list
        .filter((tpl) => recentSlugs.includes(tpl.slug))
        .sort((a, b) => recentSlugs.indexOf(a.slug) - recentSlugs.indexOf(b.slug));
    }

    return PromptCatalogService.filterTemplates({
      templates: list,
      categorySlug: selectedCategory,
      model: selectedModel,
      searchQuery: searchQuery,
    });
  }, [selectedCategory, selectedModel, searchQuery, activeTab, favorites, recentlyUsed]);

  const handleSelectTemplate = (template: EnrichedPromptTemplate) => {
    setActiveRunnerTemplate(template);
    if (onSelectTemplate) {
      onSelectTemplate(template);
    }
  };

  if (activeRunnerTemplate) {
    return (
      <PromptTemplateRunnerView
        template={activeRunnerTemplate}
        onBack={() => setActiveRunnerTemplate(null)}
        onNavigateToEditor={onNavigateToEditor}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* 1. Top Feature Spotlight: Longform Wizard Quick Banner */}
      <div className="relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-r from-primary/10 via-accent/5 to-transparent p-5 sm:p-6 shadow-sm">
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1.5 max-w-2xl">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-accent/15 text-accent border border-accent/20">
                {t('wizard.aiPipeline')}
              </span>
              <span className="text-xs text-foreground-muted font-medium">
                4-Step Context Chaining AI Engine
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-heading font-extrabold text-foreground tracking-tight">
              {t('wizard.title')}
            </h2>
            <p className="text-xs sm:text-sm text-foreground-muted leading-relaxed">
              {t('wizard.subtitle')}
            </p>
          </div>

          {onNavigateToWizard && (
            <button
              type="button"
              onClick={onNavigateToWizard}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold bg-gradient-to-r from-primary to-accent text-white shadow-glow-primary/20 hover:opacity-95 transition-all flex-shrink-0"
            >
              <SophiaIcon name="algorithm" size={16} className="text-white shrink-0" />
              <span>{t('wizard.step1')}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* 2. Category Explorer & Secondary Filter Bar */}
      <div className="p-4 sm:p-5 rounded-2xl border border-surface-border bg-surface shadow-glass space-y-4">
        <PromptCategoryFilterBar />
      </div>

      {/* 3. Results Header & Count */}
      <div className="flex items-center justify-between text-xs text-foreground-muted px-1">
        <div className="flex items-center gap-2">
          <SophiaIcon name="solution" size={16} className="text-primary shrink-0" />
          <span className="font-medium text-foreground">
            {filteredTemplates.length} templates
          </span>
          {activeTab === 'favorites' && (
            <span className="text-rose-500 font-semibold">• {t('templates.favoritesTab')}</span>
          )}
          {activeTab === 'recent' && (
            <span className="text-primary font-semibold">• {t('templates.recentTab')}</span>
          )}
        </div>

        <span className="text-[11px] font-mono">
          Dynamic Forms &bull; Streaming
        </span>
      </div>

      {/* 4. Templates Catalog Grid / Compact List */}
      {filteredTemplates.length > 0 ? (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map((template) => (
              <PromptTemplateCard
                key={template.id}
                template={template}
                onSelect={handleSelectTemplate}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-2.5">
            {filteredTemplates.map((template) => (
              <PromptTemplateCard
                key={template.id}
                template={template}
                onSelect={handleSelectTemplate}
                compact
              />
            ))}
          </div>
        )
      ) : (
        /* Empty State */
        <div className="flex flex-col items-center justify-center p-12 rounded-2xl border border-dashed border-surface-border bg-surface text-center space-y-4 shadow-inner">
          <div className="w-14 h-14 rounded-2xl bg-muted/80 text-foreground-muted flex items-center justify-center">
            <SophiaIcon name="mind" size={28} className="text-foreground-muted" />
          </div>
          <div className="space-y-1.5 max-w-md">
            <h4 className="text-base font-bold text-foreground">
              {t('templates.noTemplatesFound')}
            </h4>
          </div>
          <button
            type="button"
            onClick={resetFilters}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold bg-surface-elevated hover:bg-muted text-foreground border border-surface-border transition-colors shadow-sm"
          >
            <SophiaIcon name="algorithm" size={14} className="text-primary shrink-0" />
            <span>{t('templates.resetFiltersBtn')}</span>
          </button>
        </div>
      )}

      {/* 5. Quick Template Detail Modal Preview */}
      {activePreviewTemplate && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-150"
        >
          <div className="relative w-full max-w-xl max-h-[85vh] overflow-y-auto rounded-2xl border border-surface-border bg-surface p-6 shadow-2xl space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                    {activePreviewTemplate.category.name}
                  </span>
                  <span className="text-xs text-foreground-muted font-mono">
                    {activePreviewTemplate.modelLabel}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-foreground">
                  {activePreviewTemplate.title}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setActivePreviewTemplate(null)}
                className="p-1.5 rounded-lg border border-surface-border text-foreground-muted hover:text-foreground"
                aria-label={t('common.close')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-xs text-foreground-muted leading-relaxed">
              {activePreviewTemplate.description}
            </p>

            {/* Input Schema Preview */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs font-semibold text-foreground">
                <span className="flex items-center gap-1.5">
                  <Sliders className="w-3.5 h-3.5 text-primary" />
                  {t('templates.inputsTitle')}
                </span>
                <span className="text-foreground-muted font-normal text-[11px]">
                  {activePreviewTemplate.inputSchema.length} fields
                </span>
              </div>
              <div className="space-y-1.5 rounded-xl border border-surface-border/80 bg-surface-elevated p-3">
                {activePreviewTemplate.inputSchema.map((field, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between text-xs py-1 border-b border-surface-border/40 last:border-0"
                  >
                    <div className="space-y-0.5">
                      <span className="font-semibold text-foreground">
                        {field.label}
                      </span>
                      <span className="text-[10px] font-mono text-foreground-muted block">
                        {'{{' + field.name + '}}'}
                      </span>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded bg-surface border border-surface-border font-mono text-primary">
                      {field.type}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Prompt Template Raw Preview */}
            <div className="space-y-1.5">
              <span className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                <BookOpen className="w-3.5 h-3.5 text-primary" />
                Prompt Structure
              </span>
              <pre className="p-3 rounded-xl border border-surface-border bg-muted/60 text-[11px] font-mono text-foreground-muted whitespace-pre-wrap max-h-36 overflow-y-auto">
                {activePreviewTemplate.promptTemplate}
              </pre>
            </div>

            {/* Footer action */}
            <div className="flex items-center justify-between pt-2 border-t border-surface-border">
              <span className="inline-flex items-center gap-1 text-xs text-amber-500 font-mono font-medium">
                <Zap className="w-3.5 h-3.5 fill-amber-500" />
                {activePreviewTemplate.creditsCost} {t('common.credits')}
              </span>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActivePreviewTemplate(null)}
                  className="px-3.5 py-1.5 rounded-xl text-xs font-semibold border border-surface-border text-foreground hover:bg-muted"
                >
                  {t('common.close')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const tpl = activePreviewTemplate;
                    setActivePreviewTemplate(null);
                    if (tpl) {
                      setActiveRunnerTemplate(tpl);
                    }
                  }}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-xs font-semibold bg-primary text-primary-fg hover:bg-primary-hover shadow-sm"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>{t('templates.runTemplateBtn')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
